import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { LogOut, Shield } from "lucide-react";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const { lang, setLanguage, t } = useLang();
  const navigate = useNavigate();

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-2 group shrink-0 min-w-0"
        >
          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#FFD700] flex items-center justify-center shrink-0 p-1">
            <img src="/car-icon.svg" alt="Crypto Asset Recovery" className="w-full h-full object-contain" />
          </div>
          <span className="hidden [@media(min-width:400px)]:inline font-display font-black text-xs sm:text-lg tracking-tighter whitespace-nowrap">
            CRYPTO ASSET RECOVERY<span className="text-[#FFD700]">.</span>
          </span>
        </Link>

        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <div
            data-testid="lang-switcher"
            className="flex items-center border border-white/20 divide-x divide-white/20 text-[10px] sm:text-xs font-mono"
          >
            <button
              onClick={() => setLanguage("en")}
              data-testid="lang-en"
              className={`px-2 py-1 sm:px-2.5 sm:py-1.5 uppercase tracking-widest transition-colors ${
                lang === "en" ? "bg-[#FFD700] text-black" : "hover:bg-white/10"
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLanguage("it")}
              data-testid="lang-it"
              className={`px-2 py-1 sm:px-2.5 sm:py-1.5 uppercase tracking-widest transition-colors ${
                lang === "it" ? "bg-[#FFD700] text-black" : "hover:bg-white/10"
              }`}
            >
              IT
            </button>
          </div>

          {user ? (
            <>
              <div className="hidden lg:flex flex-col items-end">
                <span className="text-xs text-white/50 font-mono uppercase tracking-widest">
                  {user.role}
                </span>
                <span className="text-sm font-medium truncate max-w-[180px]">{user.email}</span>
              </div>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="header-admin-link"
                  className="text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2 border border-white/20 hover:bg-white/10 transition-colors"
                >
                  {t("header.admin")}
                </Link>
              )}
              <Link
                to="/dashboard"
                data-testid="header-dashboard-link"
                className="hidden sm:block text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2 border border-white/20 hover:bg-white/10 transition-colors"
              >
                {t("header.dashboard")}
              </Link>
              <button
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                data-testid="header-logout-btn"
                className="text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2 bg-[#FFD700] text-black hover:bg-[#F4C430] transition-colors flex items-center gap-1.5 font-semibold"
              >
                <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{t("header.logout")}</span>
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="text-xs sm:text-sm px-2.5 py-1.5 sm:px-4 sm:py-2 border border-white/20 hover:bg-white/10 transition-colors"
              >
                {t("header.login")}
              </Link>
              <Link
                to="/register"
                data-testid="header-register-btn"
                className="text-xs sm:text-sm px-2.5 py-1.5 sm:px-4 sm:py-2 bg-[#FFD700] text-black hover:bg-[#F4C430] transition-colors font-semibold whitespace-nowrap"
              >
                {t("header.signup")}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
